/**
 * useSelfReferralWorkflow Hook
 *
 * Manages the complete self-referral workflow including form submission,
 * session creation, dietitian matching, selection, and finalization.
 * Integrates with storage utilities for persistence and provides
 * comprehensive state management for the entire workflow.
 *
 * Features:
 * - Form data management and validation
 * - Session creation and dietitian matching
 * - Dietitian selection and referral finalization
 * - Storage integration for workflow persistence
 * - Error handling and loading states
 * - Session timer integration
 * - Automatic cleanup on unmount
 *
 * @module hooks/useSelfReferralWorkflow
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { apiService } from '@/services/ApiService';
import { referralStorage } from '@/utils/referralStorage';
import type {
  SelfReferralFormData,
  WorkflowState,
  MatchedDietitian,
  SessionResponse,
  FinalizedReferralResponse,
  UseSelfReferralWorkflowReturn
} from '@/components/self-referral/types';
import { getLogger } from '@/services/shared/LoggerFactory';

/**
 * Initial workflow state
 */
const createInitialWorkflowState = (): WorkflowState => ({
  currentStep: 'form',
  isLoading: false
});

/**
 * Custom hook for managing self-referral workflow
 *
 * @param providerId - The provider ID from URL parameters
 * @returns Workflow state and management functions
 */
export const useSelfReferralWorkflow = (providerId: string): UseSelfReferralWorkflowReturn => {
  const logger = getLogger();

  // State management
  const [workflowState, setWorkflowState] = useState<WorkflowState>(createInitialWorkflowState);
  const [error, setError] = useState<string | null>(null);
  const [timeRemaining, setTimeRemaining] = useState<number>(0);

  // Refs for cleanup and call tracking
  const isMountedRef = useRef(true);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const isSubmittingRef = useRef(false);
  const isSelectingRef = useRef(false);
  const lastSubmissionRef = useRef<{ formData: SelfReferralFormData; timestamp: number } | null>(null);
  const submitFormRef = useRef<((formData: SelfReferralFormData) => Promise<void>) | null>(null);


  /**
   * Update workflow state and persist to storage
   */
  const updateWorkflowState = useCallback((updates: Partial<WorkflowState>) => {
    setWorkflowState(prevState => {
      const newState = { ...prevState, ...updates };

      // Persist to storage
      const storageResult = referralStorage.updateWorkflow(newState);
      if (!storageResult.success) {
        console.warn('Failed to persist workflow state:', storageResult.error);
      }

      return newState;
    });
  }, []);

  /**
   * Set error state with optional automatic clearing
   */
  const setErrorWithTimeout = useCallback((errorMessage: string | null, timeout = 10000) => {
    setError(errorMessage);

    if (errorMessage && timeout > 0) {
      setTimeout(() => {
        if (isMountedRef.current) {
          setError(null);
        }
      }, timeout);
    }
  }, []);

  /**
   * Start session timer - simplified version
   */
  const startSessionTimer = useCallback((expiresAt: string) => {
    // Clear existing timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    const updateTimer = () => {
      try {
        const expirationTime = new Date(expiresAt).getTime();
        const currentTime = new Date().getTime();
        const remainingMs = expirationTime - currentTime;
        const remaining = Math.max(0, Math.floor(remainingMs / 1000));

        if (isMountedRef.current) {
          setTimeRemaining(remaining);
        }

        if (remaining <= 0) {
          // Session expired
          if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
          }

          if (isMountedRef.current) {
            setError('Your session has expired. Please start over.');
            // Reset workflow
            isSubmittingRef.current = false;
            isSelectingRef.current = false;
            lastSubmissionRef.current = null;

            const initialState = createInitialWorkflowState();
            setWorkflowState(initialState);
            setError(null);
            setTimeRemaining(0);

            referralStorage.cleanup();
          }
        }
      } catch (error) {
        console.warn('Failed to update timer:', error);
        if (isMountedRef.current) {
          setTimeRemaining(0);
        }
      }
    };

    // Update immediately
    updateTimer();

    // Set up interval
    timerRef.current = setInterval(updateTimer, 1000);
  }, []);

  /**
   * Handle session expiration (called by SessionTimer component)
   */
  const handleSessionExpire = useCallback(() => {
    setError('Your session has expired. Please start over.');
    // Reset workflow
    if (isMountedRef.current) {
      // Reset refs
      isSubmittingRef.current = false;
      isSelectingRef.current = false;
      lastSubmissionRef.current = null;

      // Reset state
      const initialState = createInitialWorkflowState();
      setWorkflowState(initialState);
      setError(null);
      setTimeRemaining(0);

      // Clear storage
      referralStorage.cleanup();
    }
  }, []);

  /**
   * Submit form and create session - Implementation
   */
  const submitFormImpl = async (formData: SelfReferralFormData): Promise<void> => {
    const callId = Math.random().toString(36).substr(2, 9);
    const currentTime = Date.now();
    logger.log(`[${callId}] submitForm called - isLoading: ${workflowState.isLoading}, isSubmittingRef: ${isSubmittingRef.current}`);

    // Check for duplicate submissions (same data within 5 seconds)
    if (lastSubmissionRef.current) {
      const timeDiff = currentTime - lastSubmissionRef.current.timestamp;
      const isSameData = JSON.stringify(formData) === JSON.stringify(lastSubmissionRef.current.formData);
      if (isSameData && timeDiff < 5000) {
        logger.log(`[${callId}] Duplicate submission detected (${timeDiff}ms ago), ignoring`);
        return;
      }
    }

    // Prevent multiple simultaneous calls using both state and ref
    if (workflowState.isLoading || isSubmittingRef.current) {
      logger.log(`[${callId}] submitForm already in progress, ignoring duplicate call`);
      return;
    }

    try {
      logger.log(`[${callId}] submitForm starting execution`);
      isSubmittingRef.current = true;
      lastSubmissionRef.current = { formData, timestamp: currentTime };
      updateWorkflowState({ isLoading: true });
      setErrorWithTimeout(null);

      logger.log(`[${callId}] Create session...`);
      // Create session
      const sessionResponse: SessionResponse = await apiService.selfReferral.createSession(providerId, formData);

      logger.log(`[${callId}] sessionResponse`, sessionResponse);

      // Update workflow state
      const newState: WorkflowState = {
        currentStep: 'selection',
        formData,
        sessionData: sessionResponse,
        isLoading: false
      };

      updateWorkflowState(newState);

      // Store in session storage with context
      const storageResult = referralStorage.store(
        newState,
        providerId,
        sessionResponse.session_token,
        sessionResponse.expires_at
      );

      if (!storageResult.success) {
        console.warn('Failed to store session data:', storageResult.error);
      }

      // Start session timer — but not when the pool returned no dietitians: there is nothing
      // to select, and letting the session expire would silently reset the patient to the
      // form and invite a second submission (design-dietitian-accreditation §4.8).
      if (sessionResponse.matched_dieticians.length > 0) {
        startSessionTimer(sessionResponse.expires_at);
      }

    } catch (error) {
      console.error('Failed to submit form:', error);

      let errorMessage = 'Failed to submit form. Please try again.';

      if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = (error as Error).message;
      }

      setErrorWithTimeout(errorMessage);
      updateWorkflowState({ isLoading: false });
    } finally {
      isSubmittingRef.current = false;
    }
  };

  // Store the implementation in ref and create stable function
  submitFormRef.current = submitFormImpl;
  const submitForm = useCallback(async (formData: SelfReferralFormData): Promise<void> => {
    if (submitFormRef.current) {
      return submitFormRef.current(formData);
    }
  }, []);

  /**
   * Select dietitian and finalize referral
   */
  const selectDietitian = useCallback(async (dietitian: MatchedDietitian): Promise<void> => {
    // Prevent multiple simultaneous calls using both state and ref
    if (workflowState.isLoading || isSelectingRef.current) {
      logger.log('selectDietitian already in progress, ignoring duplicate call');
      return;
    }

    try {
      if (!workflowState.sessionData?.session_token) {
        throw new Error('No active session found');
      }

      isSelectingRef.current = true;
      updateWorkflowState({ isLoading: true });
      setErrorWithTimeout(null);

      // Select dietitian
      const finalReferral: FinalizedReferralResponse = await apiService.selfReferral.selectDietitian(
        workflowState.sessionData.session_token,
        dietitian.dietician_id
      );

      // Update workflow state
      const newState: WorkflowState = {
        ...workflowState,
        currentStep: 'completed',
        selectedDietitian: dietitian,
        finalReferral,
        isLoading: false
      };

      updateWorkflowState(newState);

      // Stop timer as workflow is complete
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }

    } catch (error) {
      console.error('Failed to select dietitian:', error);

      let errorMessage = 'Failed to select dietitian. Please try again.';

      if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = (error as Error).message;
      }

      setErrorWithTimeout(errorMessage);
      updateWorkflowState({ isLoading: false });
    } finally {
      isSelectingRef.current = false;
    }
  }, [workflowState.sessionData?.session_token, workflowState.isLoading]);

  /**
   * Reset workflow to initial state
   */
  const resetWorkflow = useCallback(() => {
    // Clear timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }

    // Reset refs
    isSubmittingRef.current = false;
    isSelectingRef.current = false;
    lastSubmissionRef.current = null;

    // Reset state
    const initialState = createInitialWorkflowState();
    setWorkflowState(initialState);
    setError(null);
    setTimeRemaining(0);

    // Clear storage
    referralStorage.cleanup();
  }, []);

  /**
   * Load existing workflow state from storage on mount
   */
  useEffect(() => {
    const loadStoredWorkflow = () => {
      try {
        // Clear any expired data first
        referralStorage.clearExpiredData();

        const storedData = referralStorage.retrieve();

        if (storedData?.workflow && storedData.context.providerId === providerId) {
          // Validate session if we have a token
          if (storedData.context.sessionToken && storedData.workflow.sessionData) {
            // Check if session is expired
            if (referralStorage.isSessionExpired()) {
              logger.log('Stored session expired; resetting workflow');
              resetWorkflow();
              return;
            }

            // Restore workflow state
            if (isMountedRef.current) {
              setWorkflowState(storedData.workflow);
            }

            // Restart timer if we have expiration time (not for an empty-match session — see submitForm)
            const restoredMatches = storedData.workflow.sessionData.matched_dieticians ?? [];
            if (storedData.context.expiresAt && restoredMatches.length > 0) {
              startSessionTimer(storedData.context.expiresAt);
            }
          }
        }
      } catch (error) {
        logger.warn('Failed to load stored workflow', { error });
        resetWorkflow();
      }
    };

    loadStoredWorkflow();
  }, [providerId]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      isMountedRef.current = false;

      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  // Derived values
  // undefined = not yet fetched; [] = fetched, empty (design-dietitian-accreditation §4.8)
  const matches = workflowState.sessionData?.matched_dieticians;
  const sessionToken = workflowState.sessionData?.session_token || null;
  const isLoading = workflowState.isLoading;

  return {
    workflowState,
    setFormData: (data: SelfReferralFormData) => {
      updateWorkflowState({ formData: data });
    },
    matches,
    isLoading,
    error,
    submitForm,
    selectDietitian,
    sessionToken,
    timeRemaining,
    resetWorkflow,
    handleSessionExpire
  };
};

export default useSelfReferralWorkflow;